import React, { useState } from 'react';
import {
    List,
    Datagrid,
    DateField,
    FunctionField,
    Filter,
    TextInput,
    DateInput,
    SelectInput,
    TopToolbar,
    ExportButton,
    RefreshButton,
    useTranslate,
    useListContext,
    RecordContextProvider,
} from 'react-admin';
import { Box, Chip, Typography, useMediaQuery, useTheme } from '@mui/material';
import { Security, Warning, Info, CheckCircle } from '@mui/icons-material';

// Filter component for audit logs
const AuditLogFilter = (props: any) => {
    const translate = useTranslate();

    return (
        <Filter {...props}>
            <TextInput
                label={translate('resources.audit-logs.list.filters.search')}
                source="search"
                alwaysOn
            />
            <SelectInput
                label={translate('resources.audit-logs.list.filters.action')}
                source="action"
                choices={[
                    { id: 'auth.login', name: translate('resources.audit-logs.actions.auth.login') },
                    { id: 'auth.login.failed', name: translate('resources.audit-logs.actions.auth.login.failed') },
                    { id: 'auth.logout', name: translate('resources.audit-logs.actions.auth.logout') },
                    { id: 'auth.2fa.enable', name: translate('resources.audit-logs.actions.auth.2fa.enable') },
                    { id: 'auth.2fa.disable', name: translate('resources.audit-logs.actions.auth.2fa.disable') },
                    { id: 'user.create', name: translate('resources.audit-logs.actions.user.create') },
                    { id: 'user.update', name: translate('resources.audit-logs.actions.user.update') },
                    { id: 'user.delete', name: translate('resources.audit-logs.actions.user.delete') },
                    { id: 'role.create', name: translate('resources.audit-logs.actions.role.create') },
                    { id: 'role.update', name: translate('resources.audit-logs.actions.role.update') },
                    { id: 'role.delete', name: translate('resources.audit-logs.actions.role.delete') },
                ]}
            />
            <TextInput
                label={translate('resources.audit-logs.list.filters.userId')}
                source="userId"
            />
            <DateInput
                label={translate('resources.audit-logs.list.filters.startDate')}
                source="startDate"
            />
            <DateInput
                label={translate('resources.audit-logs.list.filters.endDate')}
                source="endDate"
            />
        </Filter>
    );
};

const DesktopListActions = () => (
    <TopToolbar>
        <RefreshButton />
        <ExportButton />
    </TopToolbar>
);

const MobileListActions = () => (
    <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 0.5, p: 0 }}>
        <RefreshButton />
        <ExportButton />
    </Box>
);

// Get action severity and icon
const getActionInfo = (action: string) => {
    if (action.includes('failed') || action.includes('delete')) {
        return { severity: 'error', icon: <Warning /> };
    }
    if (action.includes('login') || action.includes('logout')) {
        return { severity: 'info', icon: <Info /> };
    }
    if (action.includes('create') || action.includes('update')) {
        return { severity: 'success', icon: <CheckCircle /> };
    }
    return { severity: 'default', icon: <Security /> };
};

// Format action name for display
const formatAction = (action: string, translate: any) => {
    const translationKey = `resources.audit-logs.actions.${action}`;
    const translated = translate(translationKey);

    // If translation exists, use it; otherwise fallback to formatting
    if (translated !== translationKey) {
        return translated;
    }

    // Fallback: format the action string
    return action
        .split('.')
        .map(part => part.charAt(0).toUpperCase() + part.slice(1))
        .join(' ');
};

const MobileList = () => {
    const { data, isLoading } = useListContext();
    const translate = useTranslate();
    const [expandedId, setExpandedId] = useState<string | null>(null);

    if (isLoading || !data) return null;
    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            {data.map((record: any) => (
                <RecordContextProvider key={record.id} value={record}>
                    <Box
                        onClick={() => setExpandedId(expandedId === record.id ? null : record.id)}
                        sx={{
                            p: 2,
                            backgroundColor: '#fff',
                            borderRadius: 1,
                            boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
                            cursor: 'pointer',
                            '&:active': { backgroundColor: '#F5F5F5' },
                        }}
                    >
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                            <Chip
                                label={formatAction(record.action, translate)}
                                color={getActionInfo(record.action).severity as any}
                                size="small"
                                icon={getActionInfo(record.action).icon}
                                sx={{ fontWeight: 500 }}
                            />
                            <Typography variant="caption" color="text.secondary">
                                {record.createdAt ? new Date(record.createdAt).toLocaleString(undefined, {
                                    year: 'numeric',
                                    month: 'short',
                                    day: 'numeric',
                                    hour: '2-digit',
                                    minute: '2-digit',
                                }) : ''}
                            </Typography>
                        </Box>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {record.user?.username || record.user?.email || translate('resources.audit-logs.user.system')}
                        </Typography>
                        {record.resourceType && (
                            <Typography variant="caption" color="text.secondary">
                                {record.resourceType} {record.resourceId ? `(${record.resourceId})` : ''}
                            </Typography>
                        )}
                        {expandedId === record.id && (
                            <Box sx={{ mt: 1.5, pt: 1.5, borderTop: '1px solid #E0E0E0' }}>
                                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                                    {translate('resources.audit-logs.fields.additionalInfo')}
                                </Typography>
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                                    <Box>
                                        <Typography variant="caption" color="text.secondary">
                                            {translate('resources.audit-logs.fields.ipAddress')}
                                        </Typography>
                                        <Typography variant="body2">
                                            {record.ipAddress || '-'}
                                        </Typography>
                                    </Box>
                                    <Box>
                                        <Typography variant="caption" color="text.secondary">
                                            {translate('resources.audit-logs.fields.userAgent')}
                                        </Typography>
                                        <Typography variant="body2">
                                            {record.userAgent ? (
                                                <>
                                                    {record.userAgent.includes('Chrome') && 'Chrome'}
                                                    {record.userAgent.includes('Firefox') && 'Firefox'}
                                                    {record.userAgent.includes('Safari') && !record.userAgent.includes('Chrome') && 'Safari'}
                                                    {record.userAgent.includes('Edge') && 'Edge'}
                                                    {' / '}
                                                    {record.userAgent.includes('Windows') && 'Windows'}
                                                    {record.userAgent.includes('Mac') && 'macOS'}
                                                    {record.userAgent.includes('Linux') && 'Linux'}
                                                    {record.userAgent.includes('Android') && 'Android'}
                                                    {record.userAgent.includes('iOS') && 'iOS'}
                                                </>
                                            ) : '-'}
                                        </Typography>
                                    </Box>
                                    {record.details && (
                                        <Box>
                                            <Typography variant="caption" color="text.secondary">
                                                {translate('resources.audit-logs.fields.fullDetails')}
                                            </Typography>
                                            <Typography variant="body2" component="pre" sx={{
                                                whiteSpace: 'pre-wrap',
                                                wordBreak: 'break-word',
                                                fontSize: '0.85rem',
                                                fontFamily: 'monospace',
                                                maxWidth: '100%',
                                            }}>
                                                {JSON.stringify(record.details, null, 2)}
                                            </Typography>
                                        </Box>
                                    )}
                                </Box>
                            </Box>
                        )}
                    </Box>
                </RecordContextProvider>
            ))}
        </Box>
    );
};

export const AuditLogList = () => {
    const translate = useTranslate();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    return (
        <List
            filters={<AuditLogFilter />}
            actions={isMobile ? <MobileListActions /> : <DesktopListActions />}
            title={translate('resources.audit-logs.list.title')}
            perPage={25}
            sort={{ field: 'createdAt', order: 'DESC' }}
            sx={{
                '& .RaList-content': {
                    backgroundColor: isMobile ? 'transparent' : '#FAFAFA',
                },
                ...(isMobile && {
                    '& .RaList-main': { margin: 0 },
                    '& .RaList-content': { backgroundColor: 'transparent !important', boxShadow: 'none !important', mt: 2 },
                    '& .RaList-actions': { backgroundColor: 'transparent !important', p: '0 !important', minHeight: 'auto !important' },
                    '& .RaListToolbar-root': { backgroundColor: 'transparent !important', flexDirection: 'row !important', flexWrap: 'wrap !important', alignItems: 'center !important', gap: '8px !important' },
                    '& .RaListToolbar-root > span': { display: 'none' },
                    '& .RaFilterButton-root, & .add-filter': { display: 'none !important' },
                    '& .RaListToolbar-root > form, & .RaFilterForm-root': { flex: '1 1 100% !important', width: '100% !important' },
                    '& .filter-field, & .RaFilterForm-filterFormInput': { width: '100% !important' },
                    '& .filter-field .MuiFormControl-root': { width: '100% !important' },
                    '& .filter-field .MuiInputBase-root': { width: '100% !important' },
                    '& .MuiToolbar-root': { backgroundColor: 'transparent !important', minHeight: 'auto !important' },
                }),
            }}
        >
            {isMobile ? (
                <MobileList />
            ) : (
                <Datagrid
                    bulkActionButtons={false}
                    rowClick="expand"
                    expand={
                        <Box sx={{ p: { xs: 1.5, sm: 2 }, backgroundColor: '#F5F5F5' }}>
                            <FunctionField
                                render={(record: any) => (
                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                                            {translate('resources.audit-logs.fields.additionalInfo')}
                                        </Typography>
                                        <Box sx={{ display: 'flex', gap: { xs: 2, sm: 3 }, flexWrap: 'wrap' }}>
                                            <Box>
                                                <Typography variant="caption" color="text.secondary">
                                                    {translate('resources.audit-logs.fields.ipAddress')}
                                                </Typography>
                                                <Typography variant="body2">
                                                    {record.ipAddress || '-'}
                                                </Typography>
                                            </Box>
                                            <Box>
                                                <Typography variant="caption" color="text.secondary">
                                                    {translate('resources.audit-logs.fields.userAgent')}
                                                </Typography>
                                                <Typography variant="body2">
                                                    {record.userAgent ? (
                                                        <>
                                                            {record.userAgent.includes('Chrome') && 'Chrome'}
                                                            {record.userAgent.includes('Firefox') && 'Firefox'}
                                                            {record.userAgent.includes('Safari') && !record.userAgent.includes('Chrome') && 'Safari'}
                                                            {record.userAgent.includes('Edge') && 'Edge'}
                                                            {' / '}
                                                            {record.userAgent.includes('Windows') && 'Windows'}
                                                            {record.userAgent.includes('Mac') && 'macOS'}
                                                            {record.userAgent.includes('Linux') && 'Linux'}
                                                            {record.userAgent.includes('Android') && 'Android'}
                                                            {record.userAgent.includes('iOS') && 'iOS'}
                                                        </>
                                                    ) : '-'}
                                                </Typography>
                                            </Box>
                                            {record.details && (
                                                <Box sx={{ flex: 1 }}>
                                                    <Typography variant="caption" color="text.secondary">
                                                        {translate('resources.audit-logs.fields.fullDetails')}
                                                    </Typography>
                                                    <Typography variant="body2" component="pre" sx={{
                                                        whiteSpace: 'pre-wrap',
                                                        wordBreak: 'break-word',
                                                        fontSize: '0.85rem',
                                                        fontFamily: 'monospace',
                                                        maxWidth: '100%',
                                                    }}>
                                                        {JSON.stringify(record.details, null, 2)}
                                                    </Typography>
                                                </Box>
                                            )}
                                        </Box>
                                    </Box>
                                )}
                            />
                        </Box>
                    }
                    sx={{
                        '& .RaDatagrid-headerCell': {
                            backgroundColor: '#F5F5F5',
                            fontWeight: 600,
                        },
                        '& .RaDatagrid-row': {
                            '&:hover': {
                                backgroundColor: '#F0F7FF',
                                cursor: 'pointer',
                            },
                        },
                    }}
                >
                    <DateField
                        source="createdAt"
                        label={translate('resources.audit-logs.fields.createdAt')}
                        showTime
                        options={{
                            year: 'numeric',
                            month: 'short',
                            day: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit',
                            second: '2-digit',
                        }}
                    />

                    <FunctionField
                        label={translate('resources.audit-logs.fields.action')}
                        render={(record: any) => {
                            const { severity, icon } = getActionInfo(record.action);
                            return (
                                <Chip
                                    label={formatAction(record.action, translate)}
                                    color={severity as any}
                                    size="small"
                                    icon={icon}
                                    sx={{ fontWeight: 500 }}
                                />
                            );
                        }}
                    />

                    <FunctionField
                        label={translate('resources.audit-logs.fields.user')}
                        render={(record: any) => (
                            <Box>
                                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                    {record.user?.username || record.user?.email || translate('resources.audit-logs.user.system')}
                                </Typography>
                                {record.user?.email && record.user?.username && (
                                    <Typography variant="caption" color="text.secondary">
                                        {record.user.email}
                                    </Typography>
                                )}
                            </Box>
                        )}
                    />

                    <FunctionField
                        label={translate('resources.audit-logs.fields.resource')}
                        render={(record: any) => (
                            <Box>
                                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                    {record.resourceType || '-'}
                                </Typography>
                                {record.resourceId && (
                                    <Typography variant="caption" color="text.secondary">
                                        {record.resourceId}
                                    </Typography>
                                )}
                            </Box>
                        )}
                    />

                    <FunctionField
                        label={translate('resources.audit-logs.fields.details')}
                        render={(record: any) => {
                            if (!record.details) return (
                                <Typography variant="caption" color="text.secondary">
                                    {translate('resources.audit-logs.details.noDetails')}
                                </Typography>
                            );

                            // Format details based on action type
                            if (record.action.includes('login.failed')) {
                                return (
                                    <Chip
                                        label={record.details.reason || translate('resources.audit-logs.details.invalidCredentials')}
                                        color="error"
                                        size="small"
                                    />
                                );
                            }

                            // For other actions, show key changes
                            const details = record.details;
                            if (details.changes) {
                                // Handle both array and object format for changes
                                const changeCount = Array.isArray(details.changes)
                                    ? details.changes.length
                                    : Object.keys(details.changes).length;
                                const multipleChangesLabel = translate('resources.audit-logs.details.changes.multiple')
                                    .replace('{count}', changeCount.toString())
                                    .replace('%{count}', changeCount.toString());

                                return (
                                    <Chip
                                        label={changeCount === 1
                                            ? translate('resources.audit-logs.details.changes.single')
                                            : multipleChangesLabel}
                                        size="small"
                                        variant="outlined"
                                    />
                                );
                            }

                            return (
                                <Typography variant="caption" color="text.secondary" sx={{
                                    display: 'inline-block',
                                    maxWidth: 200,
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap'
                                }}>
                                    {JSON.stringify(details)}
                                </Typography>
                            );
                        }}
                    />
                </Datagrid>
            )}
        </List>
    );
};
